import { LenderService } from '@shared/service/lender/lender.service';
import { OAOPaymentVendor, TaskType } from '@shared/enum';
import { CrossApplicantAccess } from '@shared/model/lender-config.model';
import { TransactionResult } from 'src/postapp/models/account-funding-icg.model';
import { FundApplicationShare } from 'src/postapp/models/account-funding-swbc.model';
import { Applicant, RelationshipToApplicant } from 'src/postapp/models/applicant.model';
import { PostApplicationTask } from 'src/postapp/models/application.model';
import { CustomStipulation } from 'src/postapp/models/decision.model';
import { PostAppChecklist } from 'src/postapp/models/approved-checklist.model';
import { StatefulService } from '@app/service/stateful.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { DocumentStatus } from '@workflow/ecp-shared/enum/enum';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { IolaFeatures } from '@shared/interfaces/appInitConfig';

export class TaskCustomStipulation implements CustomStipulation {
  stipulationId: string;
  applicantId?: string;
  applicantName?: string;
  applicantType?: string;
  applicantEmail?: string;
  appStatus?: string;
  docusignUserName?: string;
  type: string;
  status: string;
  name: string;
  description?: string;
  icgTransactionResult?: TransactionResult;
  clientUserId?: string;
  isLenderCreated?: boolean;
  disableButton?: boolean;
  fundApplicationShares?: FundApplicationShare[];
  cellNumber?: string;
  isOwnTask?: boolean;
  postAppChecklist: PostAppChecklist[];
  isSeparateSign?: boolean;
  olaSettings: IolaFeatures;
  kinectiveHostSessionID?: string;
  startedSWBCPayment?: boolean;
  loggedInApplicant: Applicant;
  isTask: boolean;
  isCondition: boolean;
  private readonly applicationType: string;
  private readonly hasFailedFundingAccount_Path = 'api:oaoMemberInformation/hasFailedFundingAccount';
  constructor(
    stipulation: CustomStipulation,
    lenderService: LenderService,
    userService: UserService,
    applicationSummary: PostApplicationTask,
    protected statefulService: StatefulService,
    adminPortalFeaturesService: AdminPortalFeaturesService
  ) {
    this.stipulationId = stipulation.stipulationId;
    this.applicantId = stipulation.applicantId;
    this.isCondition = stipulation.isCondition;
    this.isTask = stipulation.isTask;
    this.applicantName = stipulation.applicantName;
    this.applicantType = stipulation.applicantType;
    this.applicantEmail = stipulation.applicantEmail;
    this.appStatus = stipulation.appStatus;
    this.name = stipulation.name;
    this.docusignUserName = stipulation.docusignUserName;
    this.type = stipulation.type;
    this.status = stipulation.status;
    this.description = stipulation.description;
    this.icgTransactionResult = stipulation.icgTransactionResult;
    this.clientUserId = stipulation.clientUserId;
    this.isLenderCreated = stipulation.isLenderCreated;
    this.disableButton = stipulation.disableButton;
    this.fundApplicationShares = stipulation.fundApplicationShares;
    this.cellNumber = stipulation.cellNumber;
    this.postAppChecklist = null;
    this.olaSettings = adminPortalFeaturesService?.config?.olaFeatures;
    this.applicationType = userService.flowType.toLowerCase();
    this.kinectiveHostSessionID = stipulation.kinectiveHostSessionID;
    this.startedSWBCPayment = stipulation.startedSWBCPayment;

    if (userService?.UserInfo?.isCellPhoneUser) {
      this.isOwnTask = userService?.UserInfo?.cellPhone === stipulation.cellNumber;
      this.loggedInApplicant = applicationSummary.applicants.find((a) =>
        a.phoneNumbers?.some((p) => p.number === userService?.UserInfo?.cellPhone)
      );
    } else {
      this.isOwnTask = userService?.UserInfo?.email?.toLowerCase() === this.applicantEmail?.toLowerCase();
      this.loggedInApplicant = applicationSummary.applicants?.find(
        (a) => a.email?.toLowerCase() === userService?.UserInfo?.email?.toLowerCase()
      );
    }

    this.seperateSign(applicationSummary);
    this.isCoapplicant = this.CheckisCoapplicant(applicationSummary, userService);
    // isapplyForMembership
    this.isapplyForMembership = this.type?.toLowerCase() === 'membership' && this.status?.toLowerCase() === DocumentStatus.Todo;

    // isMembershipSubmitted
    this.isMembershipSubmitted =
      this.type?.toLowerCase() === 'membership' &&
      (this.status.toLowerCase() === DocumentStatus.Inreview || this.status.toLowerCase() === DocumentStatus.Done);

    // showICGReviewButton
    this.showICGReviewButton = this.checkShowICGReviewButton(applicationSummary);

    // iseSignCompleted
    this.iseSignCompleted = this.isEsignTask && this.status?.toLowerCase() === DocumentStatus.Done;

    // isDocumentStatusInreview
    this.isTaskStatusInreview = this.status?.toLowerCase() === DocumentStatus.Inreview;

    // is Task Status Done
    this.isTaskStatusDone = this.status?.toLowerCase() === DocumentStatus.Done;

    // is Task Status To Do
    this.isTaskStatusToDo = this.status?.toLowerCase() === DocumentStatus.Todo || this.isIdCheckFailed;

    // isSubmitDocumentType
    this.isSubmitDocumentType = this.type?.toLowerCase() === 'submit';

    // applicantHasAccess
    this.applicantHasAccess = this.checkApplicantHasAccess(applicationSummary);

    if (!this.isEsignTask) {
      this.applicantHasAccess = true;
    }

    this.name = this.getName(stipulation.name);

    this.label = this.getLabel();

    this.disableEsignButton = this.CheckDisableEsignButton(stipulation);

    this.showSidePanel = this.checkShow(applicationSummary, lenderService, userService);
    this.isActionDisabled = this.checkDisabled(applicationSummary, lenderService.lenderData, userService);

    this.action = this.getAction();
  }

  public readonly isWithdrawnOrDeclined: boolean;
  public get showTransactionReceipt(): boolean {
    return this.showICGReviewButton;
  }

  public get isDocumentTask(): boolean {
    return this.type.toLowerCase() === 'submit';
  }

  public get isEsignTask(): boolean {
    return this.type.toLowerCase() === 'esign';
  }

  public get isFundTask(): boolean {
    return this.type.toLowerCase() === 'fund';
  }

  public get isMembershipTask(): boolean {
    return this.type.toLowerCase() === 'membership';
  }

  public get isDisbursementTask(): boolean {
    return this.type.toLowerCase() === 'disbursement';
  }

  public get isCompleted(): boolean {
    return this.status.toLowerCase() === DocumentStatus.Done;
  }

  public get isIdCheckFailed(): boolean {
    return this.status.toLowerCase() === DocumentStatus.IdCheckFailed;
  }

  public get taskStatusMessage(): string {
    if (!this.applicantName || this.type?.toLowerCase() !== TaskType.Esign) return '';
    const applicantFirstName = this.applicantName.split(' ')[0];
    const applicantLastName = this.applicantName.split(' ')[1];
    const applicantName = this.applicantType === '1' ? 'Your' : `${applicantFirstName}'s`;
    const finalApplicantName =
      this.isSeparateSign && this.applicantType !== '1' && !this.isCompleted
        ? `Your co-applicant, ${applicantFirstName} ${applicantLastName}, still needs to eSign their membership agreement to continue.`
        : `${applicantName} signature has been completed.`;
    return finalApplicantName;
  }

  private readonly iseSignCompleted: boolean;
  private readonly isapplyForMembership: boolean;

  private readonly isMembershipSubmitted: boolean;

  private isTaskStatusInreview: boolean;
  private isTaskStatusDone: boolean;
  private isTaskStatusToDo: boolean;
  private showICGReviewButton: boolean;

  private applicantHasAccess: boolean;
  private isSubmitDocumentType: boolean;
  public readonly label: string;
  public readonly action: TaskAction;
  private readonly disableEsignButton: boolean;

  private readonly showSidePanel: boolean;

  public get show() {
    return this.showSidePanel;
  }

  private readonly isActionDisabled;

  public get isDisabled() {
    return this.isActionDisabled;
  }

  checkApplicantHasAccess(applicationSummary: PostApplicationTask) {
    let applicantHasAccess = false;
    if (!this.isEsignTask) {
      applicantHasAccess = true;
    }
    if (this.isCoapplicant) return this.isOwnTask;
    const isDone = this.status.toLowerCase() === DocumentStatus.Done;
    const crossApplicantAccess = isDone ? this.olaSettings.viewDocumentsAccess : this.olaSettings.esignTasksAccess;
    const applicant = applicationSummary?.applicants?.find((a) => a.applicantId === this.applicantId);
    switch (crossApplicantAccess) {
      case CrossApplicantAccess.AllItems:
        applicantHasAccess = true;
        break;
      case CrossApplicantAccess.SpouseItems:
        if (this.loggedInApplicant?.applicantType === 'PrimaryApplicant') {
          const relationship = applicant ? applicant.relationshipToApplicant : undefined;
          applicantHasAccess = relationship === RelationshipToApplicant.JointAndSpousal ? true : this.isOwnTask;
        } else {
          applicantHasAccess =
            applicant?.applicantType === 'PrimaryApplicant' &&
            this.loggedInApplicant.relationshipToApplicant === RelationshipToApplicant.JointAndSpousal
              ? true
              : this.isOwnTask;
        }

        break;
      case CrossApplicantAccess.OwnItems:
        applicantHasAccess = this.isOwnTask;
        break;
    }
    return applicantHasAccess;
  }

  private getName(actualName) {
    if (this.isEsignTask) {
      return this.isOwnTask ? `Sign your ${actualName}` : `Sign your ${actualName} | ${this.applicantName}`;
    }

    return actualName;
  }

  private checkShowICGReviewButton(applicationSummary: PostApplicationTask) {
    const oaoEnableICGPaymentVendor = applicationSummary?.paymentConfig?.vendorType === OAOPaymentVendor.ICG.toString();

    if (
      oaoEnableICGPaymentVendor &&
      this.icgTransactionResult &&
      (this.status.toLowerCase() === DocumentStatus.Inreview || this.status.toLowerCase() === DocumentStatus.Done) &&
      this.type.toLowerCase() === 'fund'
    ) {
      return true;
    } else {
      return false;
    }
  }

  private CheckDisableEsignButton(stipulation: CustomStipulation): boolean {
    if (stipulation?.type?.toLowerCase() === 'esign' &&
      (Boolean(this.statefulService.wrap(this.hasFailedFundingAccount_Path).value)
    || this.isIdCheckFailed || this.disableButton)
    ) {
      return true;
    }
    return false;
  }

  private isDocumentStatusTodo(status: string): boolean {
    return status && status.toLowerCase() === DocumentStatus.Todo;
  }

  private isDocumentStatusDone(stipulation: CustomStipulation): boolean {
    return stipulation.status?.toLowerCase() === DocumentStatus.Done;
  }

  private checkShow(applicationSummary: PostApplicationTask, lenderService: LenderService, userService: UserService): boolean {
    // Document Upload Task
    if (this.isDocumentTask && (this.isTaskStatusInreview || this.isTaskStatusToDo)) {
      return true;
    }

    // OLA Apply for Membership
    if (
      this.applicationType === 'ola' &&
      this.isTaskStatusToDo &&
      !this.isapplyForMembership &&
      this.applicantHasAccess &&
      this.applicantHasAccessDisbursement(UserService)
    ) {
      return true;
    }

    // OAO Esign
    if (
      this.applicationType === 'oao' &&
      this.isTaskStatusToDo &&
      this.isEsignTask &&
      this.isOAOEsignTaskCanAccess(applicationSummary, lenderService.lenderData)
    ) {
      return true;
    }

    if (this.applicationType === 'oao' && this.isTaskStatusToDo && this.buttonShow(applicationSummary) && this.isFundTask) {
      return true;
    }

    // Never show "Apply for Membership" button if the user is a co-applicant
    // and that's the bottom line because Brett Ex says so
    // https://dev.azure.com/cudirect/Origence/_workitems/edit/301716
    if (this.isapplyForMembership && !this.isCoapplicant) return true;

    if (this.isMembershipSubmitted) return true;

    if (this.iseSignCompleted && this.applicantHasAccess) return true;

    if (this.showICGReviewButton) return true;

    if (this.isDisbursementTask && this.isTaskStatusToDo && this.applicantHasAccessDisbursement(userService)) return true;

    return false;
  }
  // OAO Esign When Admin configuration is sepratedsigning is true.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public isOAOEsignTaskCanAccess(applicationSummary, lenderConfig): boolean {
    if (!this.isEsignTask) {
      return true;
    }

    if (this.isCoapplicant) {
      return this.isOwnTask;
    }
    if (!this.isCoapplicant && this.isSeparateSign) {
      return this.isOwnTask;
    }
    return !this.isCoapplicant && !this.isSeparateSign;
  }
  private getLabel() {
    if (this.isTaskStatusInreview && this.isSubmitDocumentType) {
      return 'postappTasks/reupload';
    }

    if (this.type) {
      if (
        this.applicationType === 'ola' &&
        this.type.toLowerCase() === 'membership' &&
        ((this.status && this.status.toLowerCase() === DocumentStatus.Inreview) || this.status.toLowerCase() === DocumentStatus.Done)
      ) {
        return 'postappTasks/view';
      }

      if (this.applicantHasAccess && this.iseSignCompleted) {
        return 'postappTasks/view';
      }

      if (this.showICGReviewButton) {
        return 'postappTasks/review';
      }

      switch (this.type.toLowerCase()) {
        case 'submit':
          return 'postappTasks/upload';
        case 'esign':
          return 'postappTasks/sign';
        case 'disbursement':
          return 'postappTasks/disburse';
        case 'membership':
          return 'postappTasks/apply';
        case 'fund':
          return 'postappTasks/fund';
      }
    }

    return '';
  }

  private applicantHasAccessDisbursement(userService): boolean {
    if (this.type.toLowerCase() === 'disbursement') {
      if (userService?.UserInfo?.isCellPhoneUser) {
        return userService?.UserInfo?.cellPhone === this.cellNumber;
      }

      return userService?.UserInfo?.email?.toLowerCase() === this?.applicantEmail?.toLowerCase();
    }
    return true;
  }

  private checkDisabled(applicationSummary, lenderConfig, userService) {
    if (this.isTaskStatusInreview && this.isSubmitDocumentType) return this.isWithdrawnOrDeclined;

    if (
      this.applicationType === 'ola' &&
      this.isDocumentStatusTodo &&
      !this.isapplyForMembership &&
      this.applicantHasAccess &&
      this.applicantHasAccessDisbursement(userService)
    ){
      if(this.disableEsignButton && this.isEsignTask) return true;
      return this.isWithdrawnOrDeclined;
    }

    if (this.applicationType === 'oao' && this.isDocumentStatusTodo && this.buttonShow(applicationSummary) && this.isEsignTask)
      return this.disableEsignButton;

    if (this.isapplyForMembership || this.isMembershipSubmitted) return this.disableButton;

    return false;
  }

  private buttonShow(applicationSummary): boolean {
    const isFund = this?.type?.toLowerCase() === 'fund';
    if (!isFund || !this.isCoapplicant) {
      return true;
    }
    return applicationSummary.canCoApplicantFund === true;
  }

  private readonly isCoapplicant: boolean;
  private CheckisCoapplicant(applicationSummary, userService): boolean {
    const applicant = applicationSummary.applicants?.filter((t) => t.email?.toLowerCase() === userService.user?.email?.toLowerCase());
    return applicant?.length > 0 && applicant[0].applicantType !== 'PrimaryApplicant';
  }

  private seperateSign(applicationSummary) {
    this.isSeparateSign = applicationSummary.isEnabledSeparatedSigning === true;
  }

  /**
   * Set model header title
   */
  public documentPageHeader(): string {
    if (this.type?.toLowerCase() === 'disbursement') {
      return 'Disburse loan proceeds';
    }
    if (this.type?.toLowerCase() === 'fund') {
      return 'Fund your account';
    }
    if (this.type?.toLowerCase() === 'esign') {
      return this.iseSignCompleted ? 'View your document' : 'Sign your document';
    }
    return 'Submit Document';
  }

  private getAction() {
    if (this.iseSignCompleted && this.applicantHasAccess) {
      return TaskAction.selectEsignStipulation;
    }
    if (this.isapplyForMembership) {
      return TaskAction.applyForMembership;
    }
    if (this.isMembershipSubmitted) {
      return TaskAction.viewMembership;
    }
    if (this.showICGReviewButton) {
      return TaskAction.showICGPaymentInfo;
    }
    return TaskAction.upload;
  }
}

export enum TaskAction {
  upload = 'upload',
  selectEsignStipulation = 'selectEsignStipulation',
  applyForMembership = 'applyForMembership',
  viewMembership = 'viewMembership',
  showICGPaymentInfo = 'showICGPaymentInfo'
}
